import React, { useCallback, useEffect, useState } from 'react'
import { DI, DIProps } from '../../../../Core';
import { Autocomplete, Icon, SkeletonDisplayText } from '@shopify/polaris';
import { SearchMinor } from '@shopify/polaris-icons';
import { necessaryDetails } from '../Utils/Helper';
import { useTranslation } from 'react-i18next';
import { Modal } from 'antd';
import { useDebounce } from '../../useDebounce';

interface props extends DIProps {
    item: any
    row: any
    selectedOptions: string[]
    setSelectedOptions: (data: any) => void
    errorMessage: any
    setErrorMessage: (val: any) => void
}

const SearchSKU = (props: props) => {
    const { t } = useTranslation();
    const { item, selectedOptions, setSelectedOptions, errorMessage, setErrorMessage } = props;
    const [inputValue, setInputValue] = useState('');
    const [options, setOptions] = useState([]);
    const [deselectOptions, setDeselectOptions] = useState<any>([]);
    const [linked, setLinked] = useState<boolean>(false);
    const [showSkeleton, setShowSkeleton] = useState<boolean>(true);
    const [id, setId] = useState<string>("");
    const [isModalOpen, setIsModalOpen] = useState({ open: false, value: "", loader: false });

    useEffect(() => {
        getAmazonListing()
    }, [])

    /**
     * Retrieves Amazon listing details for the specified item.
     * @returns {Promise<void>} A Promise that resolves when the retrieval is complete.
     */
    const getAmazonListing = async (): Promise<void> => {
        let data: any = {};
        data = { ...necessaryDetails(props), 'data': { 'activePage': '1', 'count': '50', 'seller-sku': [item?.sku] } }
        await props.di.POST(`amazon/product/getAmazonListing`, data).then(val => {
            if (val?.success) {
                if (val?.response?.length > 0) {
                    val?.response?.forEach((ele: any) => {
                        setId(ele?.['_id']?.['$oid'])
                        if (ele?.matched && ele?.matched === true) {
                            setLinked(true)
                            setSelectedOptions([ele?.matchedProduct?.source_product_id])
                            setInputValue(ele?.matchedProduct?.source_product_id)
                        }
                        else getVariants()
                    })
                } else {
                    // console.log(first)
                    props.error(val?.message ?? (val?.response?.length <= 0 ? t("No products found") : t("Request unsuccessful")));
                    setErrorMessage({ change: true, msg: val?.message ?? (val?.response?.length <= 0 ? t("No products found") : t("Request unsuccessful")) })
                }
                setShowSkeleton(false)
            }
        })
    }

    /**
     * Retrieves variants for the current Amazon product.
     * @returns {Promise<void>} A Promise that resolves when the retrieval is complete.
     */
    const getVariants = async (): Promise<void> => {
        const temp: any = [];
        let data: any = {}
        data = { ...necessaryDetails(props), 'activePage': 1, 'count': 50, }
        if (inputValue != "") {
            let filters: any = {};
            filters = {
                'or_filter': { 'items.title': { "3": inputValue }, 'items.sku': { "3": inputValue } }
            }
            data = Object.assign({ ...data }, filters);
        }

        await props.di.POST(`amazon/product/getVariantsIncludingLinked`, data).then(val => {
            if (val?.success && val?.data) {
                val?.data?.map((data: any) => {
                    if (!["active", "inactive", "incomplete", "uploaded"].includes(data?.items?.status)) {
                        let obj: any = { label: data?.items.sku ?? "N/A", value: data?.items['source_product_id'] }
                        temp?.push(obj);
                    }

                })
                setDeselectOptions([...temp])
                setOptions(temp)
            } else props.error(t(val.error) ?? t("Request unsuccessful"));
        })
    }


    const debouncedValue = useDebounce(inputValue, 500);
    useEffect(() => {
        getVariants()

    }, [debouncedValue])


    const [loading, setloading] = useState(false)


    /**
     * Updates the input value and filters the options based on the provided value.
     * @param {string} value - The new input value.
     * @returns {void}
     */
    const updateText = useCallback((value: string): void => {
        setInputValue(value);
        if (value === '') {
            setOptions(deselectOptions);
            return;
        }
        const filterRegex = new RegExp(value, 'i');
        const resultOptions = options.filter((option: any) => option.label.match(filterRegex));
        setOptions(resultOptions);
    }, [options]);

    /**
     * Updates the selected options and sets the input value based on the selected items.
     * @param {string[]} selected - An array of selected option values.
     * @returns {void}
     */
    const updateSelection = useCallback((selected: string[]): void => {
        const selectedValue = selected.map((selectedItem) => {
            const matchedOption: any = options.find((option: any) => option.value === selectedItem);
            return matchedOption && matchedOption.label;
        });
        setSelectedOptions(selected);
        setIsModalOpen({ open: true, value: selected[0], loader: false });
        // handleManualMap(selected);
        setInputValue(selectedValue[0] || '');
    }, [options]);

    const textField: React.ReactNode = <Autocomplete.TextField
        onChange={updateText}
        label=""
        value={inputValue}
        prefix={<Icon source={SearchMinor} tone="base" />}
        placeholder="Search"
        autoComplete="off"
        disabled={linked || errorMessage.change}
    />

    /**
     * Handles manual mapping of selected product data.
     * @param {string[]} selected - An array of selected product IDs.
     * @returns {void}
     */
    const handleManualMap = (selected: string[]): void => {

        if (selected.length !== 0) {
            setloading(true)
            let data: any = {};
            data = { ...necessaryDetails(props), 'data': { _id: id, source_product_id: selected?.[0] } };
            props.di.POST(`amazon/product/manualMap`, data).then(val => {
                setloading(false)
                if (val.success) {
                    setLinked(true);
                    props.success(t(val.message));
                }
                else props.error(t(val.message) ?? t("Request unsuccessful"));
                setIsModalOpen({ open: false, value: '', loader: false })
            });
        } else props.error(t("Kindly select valid product data"));
    }

    return (
        <React.Fragment>
            {showSkeleton ? <SkeletonDisplayText size="extraLarge" /> : <Autocomplete options={options} selected={selectedOptions} onSelect={updateSelection} textField={textField} />}
            <Modal
                title={t("Link SKU")}
                open={isModalOpen.open}
                onOk={() => {
                    handleManualMap([isModalOpen.value])
                    setIsModalOpen({ ...isModalOpen, loader: true })
                }}
                okButtonProps={{ loading: loading }}
                onCancel={() => { setIsModalOpen({ open: false, value: '', loader: false }); setInputValue(""); setSelectedOptions([]) }}
                okText={t("OK")} // Custom text for OK button
                cancelText={t("Cancel")}
            >
                {t("Are you sure you want to link the SKU?")}
            </Modal>

        </React.Fragment>
    )
}

export default DI(SearchSKU);